import { Button, Link, TableCell, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import Label from "src/components/Label";
import { canDo, SignatureAction } from "src/types/signatureContext";
import { checkSupervisorPermission } from "src/utils/checkSignatureAccess";


  interface SelectSupervisiorProps {
    role: string;
    document: any;
    who: string;
    openSelectSupervisorModal: any
    from?: string
  }

const SelectSupervisior: React.FC<SelectSupervisiorProps> = ({ role, document, who, openSelectSupervisorModal, from }) => {

    const userProviderUuid = useSelector(
        (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
      );
      const getSignatureAccess = useSelector((state: any) => state.getSignatureAccessReducer);

      // THIN-7234: drive the cell off the `who` axis end-to-end so the supervisor column
      // never inherits a physician role (or vice versa). Mirrors the per-axis treatment
      // applied to signature-pending-list in THIN-7230.
      const ctx = document?.signatureContext;
      const isSupervisorAxis = who === 'supervisor';
      const axisName = isSupervisorAxis ? ctx?.supervisor?.name : ctx?.physician?.name;
      const axisUuid = isSupervisorAxis ? ctx?.supervisor?.uuid : ctx?.physician?.uuid;
      // THIN-7246: only treat the axis as "assigned" when the BE can produce a renderable
      // name. If the uuid is set but name is null (e.g. THIN-7244 archived-physician
      // degradation), fall through to the Select-affordance branch so the user can pick
      // a new assignee when CAN_SELECT_* is allowed — instead of rendering a blank cell.
      const axisAssigned = !!axisUuid && !!axisName;
      const axisRequired = isSupervisorAxis
        ? ctx?.supervisorSignatureRequired
        : ctx?.physicianSignatureRequired;

      // Whether the current user can pick a supervisor/physician for this document.
      // Prefers signatureContext (BE-owned: returns CAN_SELECT_* when current user is the
      // form author or holds DOR at the form's facility); falls back to the legacy
      // form-type-permission + author-identity check for documents predating dual-emit.
      const canSelectAssignee = (
        document: any,
        role: string,
        signatureAccess: any,
        currentUserProviderUuid: string | undefined
      ): boolean => {
        const ctx = document?.signatureContext;
        if (ctx) {
          if (role === 'Supervisor') {
            return canDo(ctx.allowedSupervisorSignatureActions, SignatureAction.CAN_SELECT_SUPERVISOR);
          }
          if (role === 'Physician') {
            return canDo(ctx.allowedPhysicianSignatureActions, SignatureAction.CAN_SELECT_PHYSICIAN);
          }
          return false;
        }
        return (
          checkSupervisorPermission(signatureAccess, document?.formType)
          && document.supervisorStatus !== null
          && document.providerId === currentUserProviderUuid
        );
      };

      const canReassign = canSelectAssignee(document, role, getSignatureAccess.response?.data?.t1, userProviderUuid);


    return (
      <>
        {
          from === 'MY_SIGN' ?
            // THIN-7257: the MY_SIGN render branch was unconditionally showing the
            // "Assign {role}" button even after a successful assignment, so the row
            // looked unchanged. Mirror the non-MY_SIGN branch — show the assignee
            // name (with reassign Link when CAN_SELECT_* is still emitted for DRAFT
            // swap, per THIN-7214) when one is assigned; show the Assign button
            // only when no assignee yet.
            axisAssigned ? (
              canReassign ? (
                <Link onClick={() => openSelectSupervisorModal(document, role)}>
                  <Typography variant="caption">{axisName}</Typography>
                </Link>
              ) : (
                <Typography variant="caption">{axisName}</Typography>
              )
            ) : (
              <Button
                onClick={() => openSelectSupervisorModal(document, role)}
                size='small'
                sx={{
                  color: ' #6366F1',
                  fontWeight: 600,
                  fontSize: '13px',
                }}
              >Assign</Button>
            ) :
            <TableCell sx={{ cursor: 'pointer' }}>
              {axisAssigned ? (
          <>
            {/* THIN-7214: when the BE emits CAN_SELECT_* even though someone is already
                assigned (FSM still in DRAFT), let the therapist/DOR click the name to swap
                to a different supervisor/physician. The BE's FSM event guard rejects
                reassignment once the doc leaves DRAFT, so this affordance disappears
                automatically after therapist sign. */}
            {canReassign ? (
              <Link onClick={() => openSelectSupervisorModal(document, role)}>
                <Typography variant="caption">{axisName}</Typography>
              </Link>
            ) : (
              <Typography variant="caption">{axisName}</Typography>
            )}

            {/* THIN-7204: read per-axis status from signatureContext rather than the legacy
                supervisorStatus DTO field (slated for removal in THIN-7205). */}
            {isSupervisorAxis &&
              (document?.signatureContext?.supervisor?.signStatus === 'COMPLETED'
                || document?.status === 'PENDING' ? (
                <Label
                      backgroundColor='rgba(17, 25, 39, 0.08)'
                      color='#6C737F'
                  text={
                    (document?.signatureContext?.supervisor?.signStatus?.slice(0, 1) ?? '') +
                    (document?.signatureContext?.supervisor?.signStatus?.slice(1).toLowerCase() ?? '')
                  }
                />
              ) : (
                <Label
                        backgroundColor='rgba(17, 25, 39, 0.08)'
                        color='#6C737F'
                  text={'Pending'}
                />
              ))}

          </>
        ) : canReassign ? (
          <Link onClick={() => openSelectSupervisorModal(document, role)}>Select {role}</Link>
        ) : axisRequired ? (
          <Typography variant="caption">Pending to be Assigned</Typography>
        ) : (
          <Typography variant="caption">Not Required</Typography>
        )}
      </TableCell>
        }
      </>

    );
  };

  export default SelectSupervisior